import { useTranslation } from "react-i18next";

import _ from "lodash";
import moment from "moment-timezone";
import styled, { css } from "styled-components";

import { Frame, TExtra } from "components/ui-kit/styled-templates";

const daysOfWeek = (t: any) => [
    t("ro2jq5jvfna"),
    t("ly6gitzeq7n"),
    t("cud2uolohgo"),
    t("zchjoelgkdr"),
    t("g5agnj9kgwe"),
    t("j1kuxxk2m5h"),
    t("yskepqf68f"),
];

interface CalendarProps {
    monthStart?: moment.Moment;
    onDateClick?: (date: moment.Moment) => void;
    selectedRange?: number[];
    showOnlyCurrentMonth?: boolean;
    allowed?: {
        from?: number;
        to?: number;
    };
}

const Calendar = (props: CalendarProps) => {
    const { t } = useTranslation();

    const { monthStart = moment(), onDateClick = () => {}, selectedRange = [], showOnlyCurrentMonth = false, allowed = {} } = props;
    const { from: allowedFrom = 0, to: allowedTo = +moment().add(1000, "year") } = allowed;

    return (
        <Frame extra={`width: calc(32px * 7); flex-direction: row; flex-wrap: wrap;`}>
            {_.times(7, (i) => (
                <Day key={i} extra={({ theme }) => `color: ${theme.text.light};`}>
                    {daysOfWeek(t)[i]}
                </Day>
            ))}
            {_.times(42, (i) => {
                const date = monthStart.clone().startOf("month").startOf("isoWeek").add(i, "days");
                const isCurrentMonth = date.month() === monthStart.month();
                const isFirstSelected = date.isSame(selectedRange[0], "day");
                const isLastSelected = date.isSame(selectedRange[1], "day");
                const isInSelectedRange = date.isBetween(selectedRange[0], selectedRange[1], "day", "[]");
                const isAllowed = date.isBetween(moment(allowedFrom), moment(allowedTo), "day", "[]");
                return (
                    <Day
                        key={i}
                        {...{
                            isCurrentMonth,
                            isFirstSelected,
                            isLastSelected,
                            isInSelectedRange,
                            date,
                            showOnlyCurrentMonth,
                            isAllowed,
                        }}
                        onClick={() => {
                            if (isAllowed) {
                                onDateClick(date);
                            }
                        }}
                    >
                        {date.format("D")}
                    </Day>
                );
            })}
        </Frame>
    );
};

const Day = styled(Frame)<{
    isCurrentMonth?: boolean;
    isFirstSelected?: boolean;
    isLastSelected?: boolean;
    isInSelectedRange?: boolean;
    isAllowed?: boolean;
    showOnlyCurrentMonth?: boolean;
    extra?: TExtra;
}>`
    width: 32px;
    height: 32px;
    font-size: 12px;
    line-height: 16px;

    ${({ theme, isCurrentMonth, isAllowed }) =>
        !isCurrentMonth || !isAllowed
            ? css`
                  color: ${theme.text.light};
              `
            : css`
                  cursor: pointer;
                  color: ${theme.text.primary};
              `};

    ${({ isCurrentMonth }) =>
        isCurrentMonth &&
        css`
            &:hover {
                opacity: 0.5;
            }
        `}
    ${({ theme, isFirstSelected, isLastSelected, isInSelectedRange }) =>
        css`
            background: ${isFirstSelected || isLastSelected ? theme.blue : isInSelectedRange ? `rgba(84, 136, 199, 0.15)` : `transparent`};
        `}

    ${({ isFirstSelected }) =>
        isFirstSelected &&
        css`
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
            color: white;
            font-weight: bold;
        `}
    ${({ isLastSelected }) =>
        isLastSelected &&
        css`
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
            color: white;
            font-weight: bold;
        `}

    ${({ showOnlyCurrentMonth, isCurrentMonth }) => showOnlyCurrentMonth && !isCurrentMonth && `visibility: hidden;`}

    ${({ extra }) => extra}
`;

export default Calendar;
